import axios from 'axios';
import { validateToken } from './reddit';
import { logger } from '../utils/logger';

export const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';
export const REDIRECT_URL = process.env.REACT_APP_REDIRECT_URL || 'https://spriglaunch.com';

// Startup check - this will be visible in browser console
const performStartupCheck = () => {
  const style = 'background: #222; color: #bada55; padding: 2px; border-radius: 2px';
  console.log('%c Spriglaunch Frontend Starting ', style);
  
  // Check environment
  console.table({
    'Environment': process.env.NODE_ENV,
    'API URL': API_URL,
    'Redirect URL': REDIRECT_URL,
    'Reddit Client ID': process.env.REACT_APP_REDDIT_CLIENT_ID ? 'Configured' : 'Missing'
  });

  // Check browser features
  console.group('Browser Features Check');
  console.log('LocalStorage:', !!window.localStorage ? '✅' : '❌');
  console.log('Cookies:', !!document.cookie ? '✅' : '❌');
  console.log('Service Worker:', !!navigator.serviceWorker ? '✅' : '❌');
  console.groupEnd();
};

// Run startup check
performStartupCheck();

// Verify critical configurations
if (!process.env.REACT_APP_API_URL && process.env.NODE_ENV === 'production') {
  console.error('⛔ Missing required configuration: API_URL');
}

if (!process.env.REACT_APP_REDIRECT_URL && process.env.NODE_ENV === 'production') {
  console.error('⛔ Missing required configuration: REDIRECT_URL');
}

if (!process.env.REACT_APP_REDDIT_CLIENT_ID && process.env.NODE_ENV === 'production') {
  console.error('⛔ Missing required configuration: REDDIT_CLIENT_ID');
}

export const API_ENDPOINTS = {
  EXTRACT_KEYWORDS: `${API_URL}/extract-keywords`,
  FETCH_POSTS: `${API_URL}/fetch-reddit-posts`,
  SUBMIT_POST: `${API_URL}/submit-post`,
  AUTH_CALLBACK: `${API_URL}/auth/callback`,
  CHECK_AUTH: `${API_URL}/api/check-auth`,
  POST_COMMENT: `${API_URL}/api/comment`,
  USER_PREFERENCES: `${API_URL}/user/preferences`,
  USER_COMMENTS: `${API_URL}/user/comments`,
  TRACK_COMMENT: `${API_URL}/comments`,
  CLICKS: `${API_URL}/api/clicks`,
  USER_TRACKING: `${API_URL}/api/user-tracking`
};

// Create axios instance with default config
export const api = axios.create({
  baseURL: API_URL,
});

// Add request interceptor
api.interceptors.request.use(async (config) => {
  const token = localStorage.getItem('reddit_token');
  
  if (token) {
    // Validate token before making request
    const isValid = await validateToken(token);
    if (!isValid) {
      // Token is invalid, trigger logout
      localStorage.removeItem('reddit_token');
      window.dispatchEvent(new Event('reddit_logout'));
      throw new Error('Invalid token');
    }
    
    // Add token to request
    config.headers.Authorization = `Bearer ${token}`;
  }
  
  return config;
}, (error) => {
  return Promise.reject(error);
});

// Add response interceptor
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response?.status === 401) {
      // Unauthorized, clear token and trigger logout
      localStorage.removeItem('reddit_token');
      window.dispatchEvent(new Event('reddit_logout'));
    }
    return Promise.reject(error);
  }
);

export const getApiUrl = (endpoint: string) => {
  if (endpoint.startsWith('http')) {
    return endpoint;
  }
  
  if (endpoint.startsWith('/')) {
    return `${API_URL}${endpoint}`;
  }
  
  return `${API_URL}/${endpoint}`;
}; 